/**
 * RFC 4180 CSV helpers used by the spreadsheet artifact pipeline.
 *
 * Parsing is intentionally tolerant of partially-streamed content so the
 * spreadsheet canvas can render rows progressively while the model is still
 * emitting CSV deltas.
 *
 * @module lib/artifacts/csv
 */

/** Parsed CSV — header row plus the data rows beneath it. */
export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  /**
   * Total cell count across header + rows. Lets the sidebar show a sensible
   * "x cells" stat without re-iterating.
   */
  cellCount: number;
}

/**
 * Parses RFC 4180 CSV into a header row and data rows.
 *
 * Tolerates a trailing partial row (no terminating newline) so the viewer
 * can render the in-flight final row as the model streams it. Empty input
 * returns an empty result rather than throwing — the canvas shows a
 * skeleton in that case.
 */
export function parseCsv(text: string): ParsedCsv {
  if (!text || text.length === 0) return { headers: [], rows: [], cellCount: 0 };

  const rows: string[][] = [];
  let current: string[] = [];
  let cell = "";
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        // Double-quote inside a quoted field is an escaped literal quote.
        if (text[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      // A quote that opens immediately, or after a delimiter, starts a
      // quoted field. Quotes mid-cell are treated as literals (lenient).
      if (cell.length === 0) {
        inQuotes = true;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }

    if (ch === ",") {
      current.push(cell);
      cell = "";
      i += 1;
      continue;
    }

    if (ch === "\n") {
      current.push(cell);
      rows.push(current);
      current = [];
      cell = "";
      i += 1;
      continue;
    }

    if (ch === "\r") {
      // CRLF or stray CR — skip, the next \n (if any) closes the row.
      i += 1;
      continue;
    }

    cell += ch;
    i += 1;
  }

  // Flush any trailing partial row so an unterminated final line still shows.
  if (cell.length > 0 || current.length > 0) {
    current.push(cell);
    rows.push(current);
  }

  if (rows.length === 0) return { headers: [], rows: [], cellCount: 0 };

  const [first, ...rest] = rows;
  const headers = first ?? [];
  let cellCount = headers.length;
  for (const row of rest) cellCount += row.length;
  return { headers, rows: rest, cellCount };
}

/** Heuristic: treat cells that look like numbers as right-aligned/tabular. */
export function looksNumeric(value: string): boolean {
  if (!value) return false;
  // Allow leading +/-, one decimal, optional thousands separators or trailing %.
  return /^[+-]?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?%?$/.test(value.trim());
}

/** Friendly label for the viewer sidebar (e.g. "1,240 rows"). */
export function formatCount(value: number, singular: string, plural?: string): string {
  const formatted = value.toLocaleString();
  return `${formatted} ${value === 1 ? singular : plural ?? `${singular}s`}`;
}
